// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
import { ENUMS } from '../../constants/enums';
// feature drivers-card
import {
  mapDriverLoadsToEmptyPeriods,
  filterDriverLoadsByLocationAndDate,
} from '../drivers-card/helpers';
// feature driver-cards-v2
import { createResourceGroupingState } from './grouping';
import {
  REQUEST_LIMIT,
  MIN_VISIBLE_DAYS,
  MAX_VISIBLE_DAYS,
  RESOURCE_MODE_TRUCK,
  RESOURCE_MODE_DRIVER,
  INITIAL_VISIBLE_DAYS,
  DEFAULT_SEARCH_RADIUS,
  DEFAULT_ADVANCED_SEARCH_RADIUS,
} from './constants';
//////////////////////////////////////////////////

const createListState = () => ({
  items: [],
  error: null,
  loading: false,
});

const createDataState = () => ({
  data: null,
  error: null,
  loading: false,
});

export const getInitialEditSession = () => ({
  dirty: false,
  saving: false,
  redoStack: [],
  undoStack: [],
  syncErrors: {},
  draftLoads: [],
  saveError: null,
  sessionId: null,
  resourceId: null,
  syncRequests: {},
  syncConflicts: {},
  baselineLoads: [],
  serverRevisionByLoadId: {},
  routeBuilderSavedToken: null,
});

export const getInitialRequestDateRange = (days = INITIAL_VISIBLE_DAYS) => {
  const sideDays = Math.floor((days - 1) / 2);
  const currentDate = G.getCurrentDateWithFormat(GC.DEFAULT_DATE_FORMAT);

  const dateTo = G.addDateTimeWithFormat(
    currentDate,
    sideDays,
    'days',
    GC.DEFAULT_DATE_FORMAT,
  );

  const dateFrom = G.subtractDateTimeWithFormat(
    currentDate,
    sideDays,
    'days',
    GC.DEFAULT_DATE_FORMAT,
  );

  return {
    dateTo: G.toEndOfDayFromDate(dateTo),
    dateFrom: G.toStartOfDayFromDate(dateFrom),
  };
};

export const getDriverCardsV2Defaults = (configs = {}) => {
  const showTeamConfig = G.getConfigValueFromStore(
    GC.UI_DRIVER_CARDS_SHOW_TEAM_DRIVER,
    configs,
  );

  const configuredDays = Number(G.getConfigValueFromStore(
    GC.UI_DRIVER_CARDS_DEFAULT_DAY_FILTER,
    configs,
  ));

  const configuredRadius = Number(G.getConfigValueFromStore(
    GC.UI_DRIVER_CARDS_DEFAULT_SEARCH_RADIUS,
    configs,
  ));

  const defaultGroupBy = G.getConfigValueFromStore(
    GC.UI_DRIVER_CARDS_DEFAULT_GROUP_BY,
    configs,
  );

  const visibleDays = Math.min(
    MAX_VISIBLE_DAYS,
    Math.max(MIN_VISIBLE_DAYS, configuredDays || INITIAL_VISIBLE_DAYS),
  );

  return {
    visibleDays,
    resourceFilter: G.getConfigValueFromStore(
      GC.UI_DRIVER_CARDS_DEFAULT_DRIVER_FILTER,
      configs,
    ) || ENUMS.ENUM_NONE,
    resourceGroupBy: G.getConfigValueFromStore(
      GC.UI_DRIVER_CARDS_DEFAULT_GROUP_FILTER,
      configs,
    ) || ENUMS.ENUM_NONE,
    resourceMode: defaultGroupBy === ENUMS.ENUM_TRUCK
      ? RESOURCE_MODE_TRUCK
      : RESOURCE_MODE_DRIVER,
    requestDateRange: getInitialRequestDateRange(visibleDays),
    showTeam: G.isBoolean(showTeamConfig) ? showTeamConfig : true,
    advancedSearchRadius: configuredRadius || DEFAULT_ADVANCED_SEARCH_RADIUS,
  };
};

const ADVANCED_SEARCH_RESOURCE_ID = '__driverCardsV2ResourceId';

export const filterResourcesByAdvancedSearch = ({
  resources,
  searchDate,
  searchRadius,
  requestDateRange,
  searchLocationResult,
  minimumEmptyHours = null,
}) => {
  const driverLoads = resources.map(({ id, source }) => ({
    ...(source || {}),
    [ADVANCED_SEARCH_RESOURCE_ID]: id,
  }));

  const driverLoadsWithEmptyPeriods = mapDriverLoadsToEmptyPeriods(
    driverLoads,
    minimumEmptyHours,
    requestDateRange,
  );

  const filteredDriverLoads = filterDriverLoadsByLocationAndDate(
    driverLoadsWithEmptyPeriods,
    searchLocationResult,
    searchRadius,
    searchDate,
  );

  const matchingResourceIds = new Set(filteredDriverLoads.map((driver) => (
    driver[ADVANCED_SEARCH_RESOURCE_ID]
  )));

  return resources.filter(({ id }) => matchingResourceIds.has(id));
};

export const getAssignedResourcesRequestData = ({
  showTeam,
  branchGuid,
  resourceSearch,
  customerFilter,
  requestDateRange,
  loadReferenceFilter = '',
  resourceFilter = ENUMS.ENUM_NONE,
  resourceGroupBy = ENUMS.ENUM_NONE,
}) => ({
  ...requestDateRange,
  showTeam,
  offset: 0,
  limit: REQUEST_LIMIT,
  filter: resourceFilter,
  groupBy: resourceGroupBy,
  currentEnterprise: branchGuid,
  enterpriseGuid: customerFilter || null,
  referenceValue: loadReferenceFilter.trim() || null,
  globalFilterValue: G.isNilOrEmpty(resourceSearch) ? null : resourceSearch,
});

export const getUnassignedLoadsRequestData = ({
  branchGuid,
  filters = {},
  requestDateRange,
}) => {
  const requestFilters = {
    ...requestDateRange,
    offset: 0,
    date: null,
    statuses: [],
    location: null,
    limit: REQUEST_LIMIT,
    enterpriseGuid: null,
    referenceValue: null,
    filter: ENUMS.ENUM_NONE,
    radius: DEFAULT_SEARCH_RADIUS,
    ...filters,
  };

  return {
    ...requestFilters,
    currentEnterprise: branchGuid,
    radiusUom: G.getMileOrKmUomByUomSystem(),
  };
};

export const getRequestError = (response, fallback) => (
  G.getPropFromObject(GC.FIELD_MESSAGE, G.getPropFromObject('data', response)) || fallback
);

export const getInitialState = () => ({
  showTeam: true,
  pageVisited: false,
  pageDefaultsLoaded: false,
  resourceSearch: '',
  customerFilter: null,
  loadReferenceFilter: '',
  selectedResourceId: null,
  unassignedPanelOpen: false,
  resourceFilter: ENUMS.ENUM_NONE,
  resourceGroupBy: ENUMS.ENUM_NONE,
  visibleDays: INITIAL_VISIBLE_DAYS,
  resourceMode: RESOURCE_MODE_DRIVER,
  advancedSearchRadius: DEFAULT_ADVANCED_SEARCH_RADIUS,
  unassignedLoads: createListState(),
  editSession: getInitialEditSession(),
  resourceGroupInfo: createDataState(),
  assignedResources: createListState(),
  requestDateRange: getInitialRequestDateRange(),
  resourceGrouping: createResourceGroupingState(),
});
